import { z } from "zod";

// Mirrors the live backend contract in migration 20261010110000_dmca.sql.
// The dmca_* tables and get_dmca_summary are not in the generated Database types
// yet (shipped after the last regen), so reads go through structural casts in
// DmcaService and are validated/typed here.

export type DmcaKind = "domain" | "url";
export type DmcaStatus = "dmca" | "clean" | null;

// get_dmca_summary() → one jsonb object. Counts are bigint → JSON numbers;
// last_check_at is a timestamptz → ISO string or null. coerce guards against a
// count arriving as a string.
export const dmcaSummarySchema = z.object({
  domains: z.coerce.number(),
  domains_tier1: z.coerce.number(),
  domains_tier2: z.coerce.number(),
  domains_tier3: z.coerce.number(),
  domains_flagged: z.coerce.number(),
  domains_clean: z.coerce.number(),
  domains_unchecked: z.coerce.number(),
  domains_failing: z.coerce.number(),
  urls_flagged: z.coerce.number(),
  urls_removed: z.coerce.number(),
  new_24h: z.coerce.number(),
  new_7d: z.coerce.number(),
  last_check_at: z.string().nullable(),
  checks_24h: z.coerce.number(),
  errors_24h: z.coerce.number(),
  // Deliberately-not-tracked tier-3 (added with migration 20261021100000).
  // Optional so the schema still parses the pre-migration summary.
  domains_untracked: z.coerce.number().optional(),
  urls_untracked: z.coerce.number().optional(),
  // URL targets per tier (added with migration 20261027135107). Optional for the
  // same reason — a summary read before that migration deploys omits them.
  urls_tier1: z.coerce.number().optional(),
  urls_tier2: z.coerce.number().optional(),
  urls_tier3: z.coerce.number().optional(),
});

export type DmcaSummary = z.infer<typeof dmcaSummarySchema>;

export const EMPTY_DMCA_SUMMARY: DmcaSummary = {
  domains: 0,
  domains_tier1: 0,
  domains_tier2: 0,
  domains_tier3: 0,
  domains_flagged: 0,
  domains_clean: 0,
  domains_unchecked: 0,
  domains_failing: 0,
  urls_flagged: 0,
  urls_removed: 0,
  new_24h: 0,
  new_7d: 0,
  last_check_at: null,
  checks_24h: 0,
  errors_24h: 0,
};

// A row of dmca_targets (the columns we select).
export interface DmcaTargetRow {
  id: number;
  kind: DmcaKind;
  target: string;
  domain: string;
  tier: 1 | 2 | 3;
  brand: string | null;
  geo: string | null;
  clicks: number;
  last_status: DmcaStatus;
  last_kind: string | null;
  last_notices: number | null;
  last_removed: number | null;
  last_notice_ids: string[];
  url_present: boolean | null;
  last_checked_at: string | null;
  flagged_since: string | null;
  error_streak: number;
}

// A row of dmca_checks (history for one target).
export interface DmcaCheckRow {
  id: number;
  checked_at: string;
  status: "dmca" | "clean" | "error";
  notices: number;
  removed: number | null;
  own_urls: number;
  url_present: boolean | null;
  error: string | null;
}

export const LUMEN_NOTICE_URL = (id: string) =>
  `https://lumendatabase.org/notices/${id}`;

// A row of dmca_overrides joined with its target (the VIP list). target/kind/
// tier/domain/brand come from the embedded dmca_targets row (PostgREST FK embed).
export interface DmcaOverrideRow {
  id: number;
  target_id: number;
  interval_hours: number;
  reason: string | null;
  active: boolean;
  created_at: string;
  target: string;
  kind: DmcaKind;
  tier: 1 | 2 | 3;
  domain: string;
  brand: string | null;
}

// A lightweight target match for the VIP add-combobox.
export interface TargetSearchRow {
  id: number;
  target: string;
  kind: DmcaKind;
  tier: 1 | 2 | 3;
  domain: string;
}

// dmca_settings (one row, id=1). Admins may UPDATE exactly these columns (RLS +
// column grant in the migration). Ranges mirror the DB CHECK constraints.
export type TierMetric = "clicks" | "revenue";

export const dmcaSettingsSchema = z.object({
  tier1_limit: z.coerce.number(),
  clicks_window_days: z.coerce.number(),
  tier1_interval_hours: z.coerce.number(),
  tier2_interval_hours: z.coerce.number(),
  tier3_interval_hours: z.coerce.number(),
  // Which metric domains are ranked into tiers by.
  tier_metric: z.enum(["clicks", "revenue"]).catch("clicks"),
});

export type DmcaSettings = z.infer<typeof dmcaSettingsSchema>;

export const DEFAULT_DMCA_SETTINGS: DmcaSettings = {
  tier1_limit: 1000,
  clicks_window_days: 30,
  tier1_interval_hours: 24,
  tier2_interval_hours: 168,
  tier3_interval_hours: 720,
  tier_metric: "clicks",
};

// DB CHECK bounds for the numeric settings, reused for client-side clamping.
// tier_metric is an enum, not clamped, so it's excluded.
export const DMCA_SETTINGS_BOUNDS: Record<
  Exclude<keyof DmcaSettings, "tier_metric">,
  { min: number; max: number }
> = {
  tier1_limit: { min: 0, max: 100000 },
  clicks_window_days: { min: 1, max: 365 },
  tier1_interval_hours: { min: 1, max: 8760 },
  tier2_interval_hours: { min: 1, max: 8760 },
  tier3_interval_hours: { min: 1, max: 8760 },
};
